import 'react-quill/dist/quill.snow.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import ReactQuill from 'react-quill';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import postProductAttributes from '../../../../../../../api/oc_product/product/attributes/post/postProductAttributes';
import parseDateString from '../../../../../../../utils/parseDateString';

function DescriptionEditor({ content, descriptionProductAttributeDetails, isTranslationTab = false }) {
  const { id } = useParams();
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const languageId = useSelector((state) => state.language?.language?.LanguageId);
  const spanRef = React.useRef(null);
  const [updated, setUpdated] = React.useState('');
  const [disableUpdate, setDisableUpdated] = React.useState(true);
  const [disableCancel, setDisableCancel] = React.useState(true);
  const [editDescription, setEditDescription] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const paragraphRef = React.useRef(null);
  const [originalContent, setOriginalContent] = React.useState('');

  const getFormattedDate = () => {
    const date = new Date();
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };
  React.useEffect(() => {
    if (spanRef.current) {
      spanRef.current.innerHTML = description;
    }
  }, [description, editDescription]);

  React.useEffect(() => {
    if (content) {
      if (content.Value !== undefined) {
        setDescription(content.Value || '');
        setUpdated(content.Updated || '');
        setOriginalContent(content.Value || '');
      } else {
        setDescription('');
        setOriginalContent('');
      }
    }
  }, [content]);

  React.useEffect(() => {
    if (editDescription) {
      setDisableUpdated(false);
      setDisableCancel(false);
      setIsEditing(true);
    }
  }, [editDescription]);

  React.useEffect(() => {
    if (paragraphRef.current && paragraphRef.current.innerHTML === '' && updated) {
      paragraphRef.current.innerHTML = parseDateString(updated);
    }
  }, [updated]);

  const resetDescription = () => {
    setEditDescription(false);
    const value = content?.Value ? content?.Value : '';
    setDescription(value);
    setDisableUpdated(true);
    setDisableCancel(true);
    setIsEditing(false);
  };

  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const updateProductDescription = () => {
    const normalizedOriginalValue = normalizeString(originalContent || '');
    const normalizedDescription = normalizeString(description);

    if (normalizedOriginalValue !== normalizedDescription) {
      if (description.trim() === '') {
        toast.error(isTranslationTab ? 'Translated description is required' : 'Description is required', {
          autoClose: 4000,
        });
        resetDescription();
      } else {
        setLoading(true);

        const targetAttributeKey = isTranslationTab
          ? 'translated_product_description'
          : 'product_description';

        const targetAttribute = descriptionProductAttributeDetails?.find(
          (attr) => attr.ProductAttributeDefKey === targetAttributeKey,
        );

        const productAttributeDefId = content?.ProductAttributeDefId
          || targetAttribute?.ProductAttributeDefId;

        const attributeDataTypeId = content?.AttributeDataTypeId
          || targetAttribute?.AttributeDataTypeId;

        const productAttributeDefKey = content?.ProductAttributeKey
          || targetAttributeKey;

        const updatedAttribute = {
          attributesValues: [
            {
              productAttributeDefKey,
              attributeDataTypeId,
              productAttributeDefId,
              value: description,
              updated: getFormattedDate(),
            },
          ],
        };

        const message = isTranslationTab
          ? 'Translated Description Updated'
          : 'Description Updated';

        if (productAttributeDefId !== 0) {
          postProductAttributes(siteId, Number(id), languageId, updatedAttribute, message)
            .then((response) => {
              setOriginalContent(description);
              setUpdated(getFormattedDate());
              setEditDescription(false);
              setIsEditing(false);
              setDisableUpdated(true);
              setDisableCancel(true);
            })
            .catch((err) => {
              console.error('Failed to update description - Full error:', err);
              console.error('Error response:', err.response);
              console.error('Error message:', err.message);
              console.error('Error stack:', err.stack);

              toast.error(isTranslationTab ? 'Failed to update translated description' : 'Failed to update description');
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          console.error('Invalid ProductAttributeDefId:', productAttributeDefId);
          toast.error('Error: Invalid attribute definition ID', {
            autoClose: 4000,
          });
          setLoading(false);
        }
      }
    } else {
      toast.error('No changes detected', {
        autoClose: 4000,
      });
    }
  };

  const handleEditClick = () => {
    setEditDescription(true);
    setIsEditing(true);
    setDisableUpdated(false);
    setDisableCancel(false);
  };

  const handleCancelClick = () => {
    resetDescription();
  };

  return (
    <div>
      {editDescription ? (
        <div className="w-full overflow-y-auto h-96">
          <ReactQuill
            value={description}
            theme="snow"
            onChange={(newValue) => {
              const val = newValue === '<p><br></p>' ? '' : newValue;
              setDescription(val);
            }}
            modules={{
              clipboard: {
                matchVisual: false,
              },
            }}
          />
        </div>
      ) : (
        <div className="w-full overflow-y-auto h-96">
          {description ? (
            <div
              onClick={handleEditClick}
              className="cursor-pointer"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            <div
              onClick={handleEditClick}
              className="cursor-pointer"
            >
              <Typography color="textSecondary" />
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mt-1 overflow-y-auto">
        <div className="flex items-center mt-3 space-x-2">
          <Stack className="mt-1">
            <Typography className="font-gothammedium" variant="body2">
              Updated:
            </Typography>
            {loading ? (
              <Skeleton width="12rem" />
            ) : (
              <Typography
                ref={paragraphRef}
                className="MuiTypography-root MuiTypography-body2 css-9v08br-MuiTypography-root"
                style={{ whiteSpace: 'nowrap' }}
              >
                {parseDateString(updated)}
              </Typography>
            )}
          </Stack>
        </div>
        <div className="flex items-center mt-3 ml-auto space-x-2">
          {!isEditing && (
            <Button
              onClick={handleEditClick}
              id="edit-button"
              variant="contained"
              size="small"
              className="px-2 py-1 mr-2 cursor-pointer max-h-8"
            >
              Edit
            </Button>
          )}
        </div>
        <div className="flex items-center mt-3 space-x-2">
          {isEditing && (
            <>
              <Button
                onClick={handleCancelClick}
                disabled={disableCancel || loading}
                id="cancel-button"
                variant="outlined"
                color="secondary"
                size="small"
                className="px-2 py-1 mr-2 cursor-pointer max-h-8"
              >
                Cancel
              </Button>
              <Button
                onClick={updateProductDescription}
                disabled={disableUpdate || loading}
                id="update-button"
                variant="contained"
                color="primary"
                size="small"
                className="px-2 py-1 mr-2 cursor-pointer max-h-8"
              >
                {loading ? 'Updating...' : 'Update'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DescriptionEditor;
